
import { ArtItem, Language } from '../types';
import { Image as ImageIcon, Zap, Maximize2, Flower, Loader2 } from 'lucide-react';
import { MASTER_FALLBACK_IMAGE } from '../constants';
import React, { useState, useEffect, useRef } from 'react';

interface ArtCardProps {
  item: ArtItem;
  onAddToCart: (item: ArtItem, signature?: string) => void;
  onPreview: (item: ArtItem) => void;
  language: Language;
}

const ArtCard: React.FC<ArtCardProps> = ({ item, onAddToCart, onPreview, language }) => {
  const [imgError, setImgError] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const [isNearScreen, setIsNearScreen] = useState(false); // Carrega imagens/pre-fetch
  const [isExactlyVisible, setIsExactlyVisible] = useState(false); // Ativa Vídeos/Animações
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Observer mais agressivo para economia de recursos
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          // Desmonta recursos pesados se estiver longe
          if (entry.isIntersecting) {
            setIsNearScreen(true);
            setIsExactlyVisible(true);
          } else {
            setIsExactlyVisible(false);
            // Mantém isNearScreen true se já foi visto para evitar "piscada" ao rolar de volta, 
            // mas removemos o vídeo no DOM abaixo baseado em isExactlyVisible.
          }
        });
      },
      { 
        threshold: 0.01, // Ativa assim que o primeiro pixel aparecer
        rootMargin: '100px' // Margem pequena para desmontagem rápida
      }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const t = {
    pt: { live: 'Obra Viva', static: 'Estático', add: 'Carrinho', nfs: 'Indisponível' },
    en: { live: 'Live Art', static: 'Static', add: 'Add to Cart', nfs: 'Not for Sale' }
  }[language];

  const isVideo = (url?: string) => {
    if (!url) return false;
    const lower = url.toLowerCase();
    return lower.includes('.mp4') || 
           lower.includes('.webm') || 
           lower.includes('giphy.com') ||
           lower.includes('blob:') ||
           lower.startsWith('data:video/');
  };

  const hasAnimation = !!item.videoUrl && !videoError;

  return (
    <div 
      ref={cardRef}
      className="art-grid-item group bg-zinc-900/40 border border-white/5 overflow-hidden transition-all duration-300 hover:border-[#ff9eb5]/50 shadow-lg flex flex-col h-full"
      style={{ transform: 'translateZ(0)' }}
    >
      <div 
        className="relative aspect-[3/4] overflow-hidden bg-black cursor-zoom-in"
        onClick={() => onPreview(item)}
      >
        {/* Camada 1: Imagem Estática (Sempre presente se "perto" da tela) */}
        {isNearScreen && (
          <img 
            src={imgError ? MASTER_FALLBACK_IMAGE : (item.imageUrl || MASTER_FALLBACK_IMAGE)} 
            alt={item.title[language]} 
            loading="lazy"
            decoding="async"
            className={`absolute inset-0 w-full h-full object-cover z-0 transition-opacity duration-500 ${isVideoLoaded ? 'opacity-0' : 'opacity-100'}`}
            onError={() => setImgError(true)}
          />
        )}

        {/* Camada 2: VÍDEO (VIRTUALIZADO - Apenas no DOM se visível) */}
        {isExactlyVisible && hasAnimation && (
          <div className="absolute inset-0 z-10 pointer-events-none bg-black">
            {isVideo(item.videoUrl) ? (
              <video 
                src={item.videoUrl}
                autoPlay 
                loop 
                muted 
                playsInline 
                preload="metadata"
                onLoadedData={() => setIsVideoLoaded(true)}
                onError={() => setVideoError(true)}
                className="w-full h-full object-cover block"
              />
            ) : (
              <img 
                src={item.videoUrl} 
                alt="Animated Artwork"
                onLoad={() => setIsVideoLoaded(true)}
                className="w-full h-full object-cover block"
                onError={() => setVideoError(true)}
              />
            )}
          </div>
        )}

        {/* Camada de Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent z-20 pointer-events-none"></div>
        
        <div className="absolute top-3 left-3 flex flex-col gap-2 z-30">
          <span className={`text-[8px] font-black px-2.5 py-1.5 flex items-center gap-1.5 uppercase tracking-widest shadow-xl ${
            item.isAnimated ? 'bg-[#ff9eb5] text-white' : 'bg-zinc-800 text-zinc-400'
          }`}>
            {item.isAnimated ? <Zap className="w-2.5 h-2.5 fill-current" /> : <ImageIcon className="w-2.5 h-2.5" />}
            {item.isAnimated ? t.live : t.static}
          </span>
        </div>

        <div className="absolute inset-0 z-40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200">
           <Maximize2 className="w-6 h-6 text-white" />
        </div>
      </div>

      <div className="p-5 bg-zinc-950/20 flex-1 flex flex-col justify-between">
        <div className="mb-4">
          <h3 className="font-black text-white group-hover:text-[#ff9eb5] transition-colors text-sm tracking-tight uppercase leading-tight line-clamp-2 min-h-[2.5rem]">
            {item.title[language]}
          </h3>
          <div className="flex justify-between items-center mt-3">
            <span className="text-[7px] text-zinc-600 font-black uppercase tracking-widest">
              {item.category}
            </span>
            <span className={`text-xs font-mono font-bold ${item.notForSale ? 'text-zinc-700' : 'text-[#ff9eb5]'}`}>
               {item.notForSale ? t.nfs : (language === 'en' ? `$ ${(item.price / 5).toFixed(2)}` : `R$ ${item.price.toFixed(2)}`)}
            </span>
          </div>
        </div>
        
        {!item.notForSale && (
          <button 
            onClick={(e) => { e.stopPropagation(); onAddToCart(item); }}
            className="w-full bg-white hover:bg-[#ff9eb5] text-black hover:text-white font-black py-3 flex items-center justify-center gap-2 transition-all tracking-widest text-[8px] uppercase active:scale-95"
          >
            <Flower className="w-3.5 h-3.5" />
            {t.add}
          </button>
        )}
      </div>
    </div>
  );
};

export default ArtCard;
